import { useMemo } from 'react';
import { primitiveColorTokensV2, primitiveColorTokensV2CssVars, semanticColorTokensV2CssVars, semanticColorTokensV2Light, semanticColorTokensV2Dark, semanticColorTokensV2EatsLight, semanticColorTokensV2EatsDark, semanticColorTokensV2AiLight, semanticColorTokensV2AiDark, semanticColorTokensV2OffersLight, semanticColorTokensV2OffersDark } from '@uber/base-specs/src/generated/base-color-tokens-v2';
import { elevationTokensV2Light, elevationTokensV2Dark } from '@uber/base-specs/src/generated/base-elevation-tokens-v2';
import { cornerRadiusTokensV2 } from '@uber/base-specs/src/generated/base-corner-radius-tokens-v2';
import { getV1ColorBaseline } from '../../themes/v1-color-baselines';
import type { Theme } from '../../styles';
import type { Elevation, CornerRadius } from '../../tokens';
import type { Base2Features, ThemeMode, BaseProduct } from '../types';
import { mergeV2Colors, upgradeV1TypographyToV2 } from './upgraders';
export { isV2PrimitiveKey, mergeV2Colors, renameCollidingV2Keys, upgradeV1ColorsToV2, upgradeV1ComponentColorsToV2, upgradeV1TypographyToV2 } from './upgraders';

/** Cache key for a mode/product token set; either half may be absent. */
const tokenSetKey = (themeMode: ThemeMode | undefined, product: BaseProduct | undefined): string => `${themeMode ?? ''}|${product ?? ''}`;
const rawV2ColorsCache = new Map<string, Record<string, string>>();
const rawV2ElevationCache = new Map<string, Elevation>();

/**
 * Builds the raw JS v2 color set for a given mode and product.
 * Result contains actual hex / rgba values — no CSS custom-property
 * references — so it works without any DOM data attributes.
 */
function buildRawV2Colors(themeMode: ThemeMode | undefined, product: BaseProduct | undefined): Record<string, string> {
  const p = (primitiveColorTokensV2 as Record<string, string>);
  const isDark = themeMode === 'dark';

  // Start from the base semantic set for the active mode.
  const base = isDark ? semanticColorTokensV2Dark(p) : semanticColorTokensV2Light(p);

  // Apply product overrides (sparse — only tokens that differ from the base).
  let productOverrides: Record<string, string> = {};
  if (product === 'eats') {
    productOverrides = isDark ? semanticColorTokensV2EatsDark(p) : semanticColorTokensV2EatsLight(p);
  } else if (product === 'ai') {
    productOverrides = isDark ? semanticColorTokensV2AiDark(p) : semanticColorTokensV2AiLight(p);
  } else if (product === 'offers') {
    productOverrides = isDark ? semanticColorTokensV2OffersDark(p) : semanticColorTokensV2OffersLight(p);
  }

  // `Object.assign`, not `{...p, ...base, ...productOverrides}`. Where a build
  // still transpiles spread, Babel lowers a multi-source one to
  // `_objectSpread2`, which calls `Object.defineProperty` once per key —
  // pathological across ~520 keys. Bundles emitting native spread are
  // unaffected; the semantics are identical for string maps into a fresh
  // target, so this is the cheaper option in both cases.
  return Object.assign({}, p, base, productOverrides);
}

/**
 * The v2 color set for a mode and product, built at most once per combination.
 *
 * Treat the result as read-only — it is shared by every theme resolved for the
 * same combination. The cache is bounded by a closed set of combinations (2
 * modes x 4 product treatments) built from module constants, and is filled
 * lazily so apps that never enable Base 2.0 colors never build one.
 */
function getRawV2Colors(themeMode: ThemeMode | undefined, product: BaseProduct | undefined): Record<string, string> {
  const key = tokenSetKey(themeMode, product);
  let cached = rawV2ColorsCache.get(key);
  if (cached === undefined) {
    cached = Object.freeze(buildRawV2Colors(themeMode, product));
    rawV2ColorsCache.set(key, cached);
  }
  return cached;
}
let cssVarV2Colors: Record<string, string> | undefined;

/**
 * The v2 color set for the CSS path: `var(--base-color-*)` references that
 * resolve against whatever `data-theme` / `data-product` is in scope, which is
 * why — unlike the JS path — it needs no mode or product of its own.
 *
 * `colorOverrides` are the colors the app passed to `createTheme`, re-applied
 * on top. Without them the rename pass, which writes every v2 key
 * unconditionally, would overwrite an app's customized semantic colors with
 * the v2 var. Themes with no color overrides share the base set untouched.
 *
 * Frozen for the same reason as {@link getRawV2Elevation}'s cache: the
 * no-overrides branch below hands back the shared reference directly, and
 * this function is exported (tests already call it), so nothing prevents an
 * accidental in-place edit from landing on every theme sharing it.
 *
 * @internal Exported for tests only; not part of the public API.
 */
export function getCssVarV2Colors(colorOverrides?: Partial<Record<string, string>>): Record<string, string> {
  if (cssVarV2Colors === undefined) {
    cssVarV2Colors = Object.freeze(Object.assign({}, primitiveColorTokensV2CssVars, semanticColorTokensV2CssVars));
  }
  return colorOverrides ? Object.assign({}, cssVarV2Colors, colorOverrides) : cssVarV2Colors;
}

/**
 * Builds the raw JS v2 elevation set for a given mode. Values are literal
 * `box-shadow` strings (no `var(--base-elevation-*)` references), so shadows
 * paint without `base-elevation-v2.css` or any DOM data attribute. Dark-elevated
 * has no `themeMode` analog and remains a CSS-path-only treatment.
 */
function buildRawV2Elevation(themeMode: ThemeMode | undefined): Elevation {
  const source = themeMode === 'dark' ? elevationTokensV2Dark : elevationTokensV2Light;
  return {
    shadowLowest: source.elevationLowest,
    shadowLow: source.elevationLow,
    shadowMid: source.elevationMid,
    shadowHigh: source.elevationHigh,
    shadowHighest: source.elevationHighest
  };
}

/**
 * The v2 elevation set for a mode, built at most once and frozen.
 *
 * Frozen rather than documented as read-only because this object reaches
 * consumers directly as `theme.elevation`: an in-place edit would otherwise
 * land on every theme sharing the mode.
 */
function getRawV2Elevation(themeMode: ThemeMode | undefined): Elevation {
  const key = tokenSetKey(themeMode, undefined);
  let cached = rawV2ElevationCache.get(key);
  if (cached === undefined) {
    cached = Object.freeze(buildRawV2Elevation(themeMode));
    rawV2ElevationCache.set(key, cached);
  }
  return cached;
}
type UpgradeInputs = {
  theme: Theme;
  colors: boolean;
  elevation: boolean;
  cssTheming: boolean;
  themeMode: ThemeMode | undefined;
  product: BaseProduct | undefined;
  typography: boolean;
};

/**
 * Resolves one feature/mode/product combination against a theme, returning a
 * new theme with v2 tokens applied and the `v1Colors` / `v2Colors` /
 * `v1Typography` baselines cleared — they exist only to drive this step.
 *
 * Assumes at least one v2 feature is enabled; the caller short-circuits when
 * none are. Callers should go through the hook, which caches these results
 * (see {@link upgradedThemeCache}) rather than recomputing per render.
 *
 * @internal Exported for tests only; not part of the public API.
 */
export function computeUpgradedTheme({
  theme,
  colors,
  elevation,
  cssTheming,
  themeMode,
  product,
  typography
}: UpgradeInputs): Theme {
  let mergedColors = theme.colors;
  if (colors) {
    const v2Colors = cssTheming ? theme.v2Colors ?? getCssVarV2Colors(theme.colorOverrides) : getRawV2Colors(themeMode, product);
    // Built-in themes carry no baseline of their own — it is the same map for
    // every theme of a mode, so it is resolved from the default theme. A
    // hand-built theme that supplies one still wins.
    mergedColors = (mergeV2Colors(theme.colors, theme.v1Colors ?? getV1ColorBaseline(theme), v2Colors, themeMode) as Theme['colors']);
  }

  // CSS path keeps the var(--base-elevation-*) composites already on the
  // theme; JS path swaps in the literal box-shadow set for the active mode.
  let mergedElevation = theme.elevation;
  if (elevation && !cssTheming) {
    mergedElevation = getRawV2Elevation(themeMode);
  }

  // CSS path keeps the var(--base-corner-radius-*) references already on the
  // theme; JS path swaps in the literal px set. This is not gated on the
  // `cornerRadius` flag: theme.cornerRadius is always var() references, which
  // only resolve when base-corner-radius-v2.css is present — the exact thing
  // the JS path does not assume — so any JS-path consumer needs the literals.
  // Corner radius has no modes, so the JS set is mode-independent.
  let mergedCornerRadius = theme.cornerRadius;
  if (!cssTheming) {
    mergedCornerRadius = (cornerRadiusTokensV2 as CornerRadius);
  }

  // The v2 ramp's own Headline/Body-named keys (`headlineXlBold`,
  // `bodyLRegular`, ...) are always present on `theme.typography` — see
  // `themes/shared/typography.ts` — independent of this flag. Only the
  // v1-named style migration (`HeadingLarge` → v2, etc.) is gated here.
  let mergedTypography = theme.typography;
  if (typography) {
    // Without a `v1Typography` baseline there's no way to tell an
    // untouched default from a deliberate override, so — mirroring the
    // `v1Colors`-absent fallback above — the named-style migration is
    // skipped entirely rather than risk clobbering a customization.
    const upgradedNamedStyles = theme.v1Typography ? upgradeV1TypographyToV2(theme.typography, theme.v1Typography, cssTheming) : {};
    mergedTypography = (Object.assign({}, theme.typography, upgradedNamedStyles) as Theme['typography']);
  }
  return Object.assign({}, theme, {
    colors: mergedColors,
    elevation: mergedElevation,
    cornerRadius: mergedCornerRadius,
    typography: mergedTypography,
    v1Colors: undefined,
    v2Colors: undefined,
    v1Typography: undefined,
    colorOverrides: undefined
  });
}
const MAX_CACHED_THEMES = 100;

/**
 * LRU cache of themes for performance
 */
const upgradedThemeCache = new Map<Theme, Map<string, Theme>>();
function getCachedCombinations(theme: Theme): Map<string, Theme> {
  const existing = upgradedThemeCache.get(theme);
  if (existing !== undefined) {
    // Re-insert to mark most-recently-used; `Map` iterates in insertion order.
    upgradedThemeCache.delete(theme);
    upgradedThemeCache.set(theme, existing);
    return existing;
  }
  const created = new Map<string, Theme>();
  upgradedThemeCache.set(theme, created);
  if (upgradedThemeCache.size > MAX_CACHED_THEMES) {
    const oldestKey = upgradedThemeCache.keys().next().value;
    if (oldestKey !== undefined) upgradedThemeCache.delete(oldestKey);
  }
  return created;
}
const upgradeCacheKey = (base2Features: Base2Features, cssTheming: boolean, themeMode: ThemeMode | undefined, product: BaseProduct | undefined): string => `${+base2Features.colors}${+base2Features.elevation}${+base2Features.material}` + `${+base2Features.cornerRadius}${+base2Features.typography}${+cssTheming}` + `|${themeMode ?? ''}|${product ?? ''}`;

/**
 * Bridges `Base2Features` flags into the `Theme` handed to `ThemeProvider`.
 *
 * The returned reference is stable for the same theme and inputs, and callers
 * may rely on that: Styletron memoizes styles by theme identity, so a new
 * object here would restyle every descendant on each parent re-render. Apps
 * with no v2 features enabled get their `theme` back untouched.
 *
 * Pass a stable `theme` — every cache on this path keys on its identity, so a
 * theme rebuilt each render misses all of them. See `createLightTheme`.
 */
export const useThemeWithPotentialUpgrades = ({
  theme,
  base2Features,
  cssTheming = false,
  themeMode,
  product
}: {
  theme: Theme;
  base2Features: Base2Features;
  cssTheming?: boolean;
  themeMode?: ThemeMode;
  product?: BaseProduct;
}) => {
  const {
    colors,
    elevation,
    material,
    cornerRadius,
    typography
  } = base2Features;
  return useMemo(() => {
    const hasAnyV2 = colors || elevation || material || cornerRadius || typography;
    if (!hasAnyV2) return theme;

    // `theme` is typed as required but `BaseProvider`'s prop is sometimes
    // unset at runtime.
    if (theme == null) return theme;
    const inputs: UpgradeInputs = {
      theme,
      colors,
      elevation,
      cssTheming,
      themeMode,
      product,
      typography
    };
    const byCombination = getCachedCombinations(theme);
    const key = upgradeCacheKey(base2Features, cssTheming, themeMode, product);
    let upgraded = byCombination.get(key);
    if (upgraded === undefined) {
      upgraded = computeUpgradedTheme(inputs);
      byCombination.set(key, upgraded);
    }
    return upgraded;
    // `base2Features` is read only through the flags already listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, colors, elevation, material, cornerRadius, typography, cssTheming, themeMode, product]);
};